import { cn } from '@/lib/cn';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { SidebarGroup, SidebarMenu } from '@heidi/design-system/page-shell';
import { focusRing } from '@heidi/design-system/styles';
import { typographyVariant } from '@heidi/design-system/typography';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { navV3Messages } from '../nav-v3.messages';
import type { RecentsDateGroup } from './recents-date-group';

type RecentsDateGroupHeaderProps = {
  group: RecentsDateGroup;
  label: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Rendered after the rows, inside the collapsible content, so it hides
   *  together with them when the group is collapsed. */
  footer?: ReactNode;
  /** The Home hover preview's reuse of this component (`RecentsSectionBody`'s
   *  `isCompact`, threaded one level further). The persistent sidebar panel
   *  never passes this, so its inset stays exactly `px-2` — unchanged from
   *  `main` — while the compact preview widens to `px-3`, matching the
   *  rows' own `AGENT_HOME_NAV_ROW` inset. */
  isCompact?: boolean;
};

export const RecentsDateGroupHeader = ({
  group,
  label,
  isOpen,
  onOpenChange,
  children,
  footer,
  isCompact = false,
}: RecentsDateGroupHeaderProps) => {
  const intl = useIntl();

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <SidebarGroup className="p-0">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={intl.formatMessage(
              isOpen
                ? navV3Messages.collapseRecentsDateGroup
                : navV3Messages.expandRecentsDateGroup,
              { section: label }
            )}
            data-testid={`sidebar-recents-group-${group}`}
            className={cn(
              'group/recents-date flex h-8 w-full items-center gap-1 overflow-hidden whitespace-nowrap rounded-md',
              isCompact ? 'px-3' : 'px-2',
              'text-text-secondary',
              focusRing,
              'transition-colors duration-200 ease-out motion-reduce:transition-none',
              'hover:text-text-primary'
            )}
          >
            <span
              className={cn(
                typographyVariant({ variant: 'caption' }),
                'min-w-0 flex-1 truncate text-start'
              )}
            >
              {label}
            </span>
            {/* size-7 slot matches the Recents header's 28px icon buttons so both share one right edge. */}
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center"
            >
              {/* Hidden at rest only for hover-capable pointers, so touch keeps the affordance. */}
              <ChevronDown
                data-testid={`sidebar-recents-group-${group}-chevron`}
                className={cn(
                  'size-4 text-text-secondary pointer-fine:sm:opacity-0',
                  'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                  'group-hover/recents-date:opacity-100 group-focus-visible/recents-date:opacity-100',
                  !isOpen && '-rotate-90 rtl:rotate-90'
                )}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        {/* No `will-change` here unlike the two section headers: this
            component renders once per loaded date group, an unbounded count
            as the user loads more, so a permanent compositor hint on every
            instance is unbounded resource use rather than a fixed cost. */}
        <CollapsibleContent className="overflow-hidden motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down">
          <SidebarMenu>{children}</SidebarMenu>
          {footer}
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
};
